import { useCallback, useState } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import {
    buildClientDossierWorkbook,
    clientDossierFilename,
} from '@/lib/dashboard/clients/client-dossier.export';
import type { ClientDetailResult } from '@/integrations/supabase/rpc-types';

interface UseClientDossierExportResult {
    /** Vero mentre exceljs scende e il workbook si costruisce: il bottone resta disabilitato. */
    exporting: boolean;
    exportDossier: (data: ClientDetailResult) => Promise<void>;
}

/**
 * Scarica la scheda cliente come XLSX.
 *
 * Non rilegge nulla dal database: esporta esattamente il `ClientDetailResult` che il
 * pannello ha gia' in mano, quindi il file e' per costruzione coerente con quello che
 * l'utente sta guardando, interruttore del periodo compreso. Una seconda fetch potrebbe
 * tornare con numeri diversi — un incasso registrato nel frattempo — e il file
 * smentirebbe lo schermo senza che nessuno capisca perche'.
 */
export function useClientDossierExport(): UseClientDossierExportResult {
    const [exporting, setExporting] = useState(false);
    const { t, language } = useI18n();
    const money = useMoneyCopy();

    const exportDossier = useCallback(
        async (data: ClientDetailResult) => {
            setExporting(true);
            try {
                const blob = await buildClientDossierWorkbook(data, {
                    t,
                    money,
                    locale: language === 'en' ? 'en-GB' : 'it-IT',
                });

                const filename = clientDossierFilename(
                    data,
                    t('dashboard.overview.clients.export.filenamePrefix')
                );

                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = filename;
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                // Rilasciato al giro dopo: revocarlo subito annulla il download su Safari,
                // che legge l'URL dopo il click e non durante.
                setTimeout(() => URL.revokeObjectURL(url), 0);

                toast({
                    title: t('dashboard.overview.clients.export.successTitle'),
                    description: filename,
                });
            } catch (error) {
                console.error('Client dossier export failed:', error);
                // Un payload cambiato e un errore di scrittura non si curano allo stesso
                // modo: il primo e' una regressione da segnalare a chi tiene la RPC, il
                // secondo un intoppo da riprovare. Il messaggio lo dice.
                const payloadIssue =
                    error instanceof Error && error.name === 'DossierPayloadError';
                toast({
                    title: t('common.error'),
                    description: t(
                        payloadIssue
                            ? 'dashboard.overview.clients.export.errorPayload'
                            : 'dashboard.overview.clients.export.errorBody'
                    ),
                    variant: 'destructive',
                });
            } finally {
                setExporting(false);
            }
        },
        [t, money, language]
    );

    return { exporting, exportDossier };
}

export default useClientDossierExport;
